import { defineStore } from "pinia"
import { Business, BusinessConnection, BusinessStatus } from "~/models/business"
import { ApiResponse } from "~/types/api"

export type BusinessStatusType = {
	name: string
	value: BusinessStatus | any
	borderClass?: string
	bgClass?: string
	txtClass?: string
}

export const useBusinessStore = defineStore("business", () => {
	const { $i18n } = useNuxtApp()

	const state = {
		businesses: ref<Business[] | null>(null),
		business: ref<Business | null>(null),
		statuses: ref<BusinessStatusType[]>([
			{
				name: $i18n.t("open"),
				value: "open",
				txtClass: "text-green-500",
				borderClass: "border-green-500",
				bgClass: "bg-green-500"
			},
			{
				name: $i18n.t("close"),
				value: "close",
				txtClass: "text-red-500",
				borderClass: "border-red-500",
				bgClass: "bg-red-500"
			},
			{
				name: $i18n.t("trash"),
				value: "trash",
				txtClass: "text-red-500",
				borderClass: "border-red-500",
				bgClass: "bg-red-500"
			}
		])
	}

	const getBusiness = async (id: number, params?: GetBusinessRequest) => {
		const data = await $fetch<GetBusinessResponse>(GetBusiness.url(id), {
			...useFetchConfig(),
			method: GetBusiness.method,
			params
		})
		return data.data
	}

	const getTempBusiness = async () => {
		const data = await $fetch<GetTempBusinessResponse>(GetTempBusiness.url(), {
			...useFetchConfig(),
			method: GetTempBusiness.method
		})
		return data.data
	}

	const getBusinessBySlug = async (slug: string, params?: GetBusinessBySlugRequest) => {
		const data = await $fetch<GetBusinessBySlugResponse>(GetBusinessBySlug.url(slug), {
			...useFetchConfig(),
			method: GetBusinessBySlug.method,
			params
		})
		return data.data
	}

	const searchBusinesses = async (formData: SearchBusinessesRequest) => {
		const data = await $fetch<SearchBusinessesResponse>(SearchBusinesses.url(), {
			...useFetchConfig(),
			method: SearchBusinesses.method,
			query: formData
		})
		return data.data
	}

	const createBusiness = async (formData: CreateBusinessRequest) => {
		const data = await $fetch<CreateBusinessResponse>(CreateBusiness.url(), {
			...useFetchConfig(),
			method: CreateBusiness.method,
			body: formData
		})
		return data.data
	}

	const editBusiness = async (formData: EditBusinessRequest) => {
		const data = await $fetch<EditBusinessResponse>(EditBusiness.url(), {
			...useFetchConfig(),
			method: EditBusiness.method,
			body: formData
		})
		return data.data
	}

	const deleteBusiness = async (id: number) => {
		const data = await $fetch<DeleteBusinessResponse>(DeleteBusiness.url(id), {
			...useFetchConfig(),
			method: DeleteBusiness.method
		})
		return data.data
	}

	const connect = async (formData: BusinessConnectRequest) => {
		const data = await $fetch<BusinessConnectResponse>(BusinessConnect.url(), {
			...useFetchConfig(),
			method: BusinessConnect.method,
			body: formData
		})
		return data.data
	}

	const disconnect = async (formData: BusinessDisconnectRequest) => {
		const data = await $fetch<BusinessDisconnectResponse>(BusinessDisconnect.url(), {
			...useFetchConfig(),
			method: BusinessDisconnect.method,
			body: formData
		})
		return data.data
	}

	return {
		...state,
		getBusiness,
		getTempBusiness,
		getBusinessBySlug,
		searchBusinesses,
		createBusiness,
		editBusiness,
		deleteBusiness,
		connect,
		disconnect
	}
})

// business :: create business
export interface CreateBusinessRequest {
	name?: string
	content?: string
	hours?: string
	status?: string
	slug: string
	category_id?: number | null
	image_id?: number | null
	cover_id?: number | null
	longitude?: number | null
	latitude?: number | null
	email?: string | null
	mobile?: string | null
	website?: string | null
	is_open_for_business?: boolean | null
	is_delivery_available?: boolean | null
}
export interface CreateBusinessResponse extends ApiResponse {
	data: Business
}
const CreateBusiness = {
	method: "POST" as "POST",
	url: () => "businesses/create"
}

// business :: edit business
export interface EditBusinessRequest {
	id: number
	name?: string
	slug: string
	content?: string
	hours?: string
	category_id?: number | null
	longitude?: number | null
	latitude?: number | null
	image_id?: number | null
	cover_id?: number | null
	email?: string
	mobile?: string
	website?: string
	status?: BusinessStatus
	is_open_for_business?: boolean
	is_delivery_available?: boolean
	moderation_status?: string
	moderation_reject_reason?: string
}
export interface EditBusinessResponse extends ApiResponse {
	data: Business
}
const EditBusiness = {
	method: "POST" as "POST",
	url: () => "businesses/edit"
}

// business :: delete business
export interface DeleteBusinessRequest {}
export interface DeleteBusinessResponse extends ApiResponse {
	data: null
}
const DeleteBusiness = {
	method: "POST" as "POST",
	url: (id: any) => `businesses/delete/${id}`
}

// business :: get business
export interface GetBusinessRequest {
	moderation_statuses?: string
	user_moderation_statuses?: string
	is_moderated?: boolean
}
export interface GetBusinessResponse extends ApiResponse {
	data: Business
}
const GetBusiness = {
	method: "GET" as "GET",
	url: (id: any) => `businesses/${id}`
}

// business :: get temp business
export interface GetTempBusinessRequest {}
export interface GetTempBusinessResponse extends ApiResponse {
	data: Business
}
const GetTempBusiness = {
	method: "POST" as "POST",
	url: () => `businesses/temp`
}

// business :: get business by slug
export interface GetBusinessBySlugRequest {
	moderation_statuses?: string
	user_moderation_statuses?: string
	is_moderated?: boolean
}
export interface GetBusinessBySlugResponse extends ApiResponse {
	data: Business
}
const GetBusinessBySlug = {
	method: "GET" as "GET",
	url: (slug: any) => `businesses/slug/${slug}`
}

// business :: search businesses
export interface SearchBusinessesRequest {
	search?: string
	page?: number
	limit?: number
	status?: BusinessStatus
	category_id?: number | null
	category_slug?: string | null
	user_id?: number | null
	neighbourhood?: string | null
	order_by?: string
	direction?: string
	locale?: string
	is_connected?: boolean
	moderation_statuses?: string
	user_moderation_statuses?: string
	is_moderated?: boolean
	is_blocked?: boolean
}
export interface SearchBusinessesResponse extends ApiResponse {
	data: {
		businesses: Business[]
		total: number
	}
}
const SearchBusinesses = {
	method: "GET" as "GET",
	url: () => `businesses/search`
}

// business :: connect
export interface BusinessConnectRequest {
	business_id: number
}
export interface BusinessConnectResponse extends ApiResponse {
	data: BusinessConnection
}
const BusinessConnect = {
	method: "POST" as "POST",
	url: () => `businesses/connect`
}

// business :: disconnect
export interface BusinessDisconnectRequest {
	business_id: number
}
export interface BusinessDisconnectResponse extends ApiResponse {
	data: null
}
const BusinessDisconnect = {
	method: "POST" as "POST",
	url: () => `businesses/disconnect`
}

export const useBusinessApiUrl = () => ({
	CreateBusiness,
	EditBusiness,
	DeleteBusiness,
	GetBusiness,
	GetBusinessBySlug,
	SearchBusinesses,
	BusinessConnect,
	BusinessDisconnect
})
